import { useEffect, useMemo, useState } from 'react';
import { useMutation } from 'urql';
import StickyActions from '@/components/StickyActions.jsx';
import { Field } from '@/components/Field.jsx';
import { useToast } from '@/hooks/useKeyboardShortcuts.js';
import { CREAR_CENTRO, ACTUALIZAR_CENTRO } from './gql.js';

export default function CentroForm({ centro, empresaId, centros, onSuccess, onCancel }) {
  const isEdit = Boolean(centro?.id);
  const toast = useToast();
  const [form, setForm] = useState({
    codigo: '',
    nombre: '',
    padreId: '',
    activo: true,
  });
  const [, crearCentro] = useMutation(CREAR_CENTRO);
  const [, actualizarCentro] = useMutation(ACTUALIZAR_CENTRO);

  useEffect(() => {
    if (centro) {
      setForm({
        codigo: centro.codigo ?? '',
        nombre: centro.nombre ?? '',
        padreId: centro.padreId ?? '',
        activo: centro.activo !== false,
      });
    } else {
      setForm({ codigo: '', nombre: '', padreId: '', activo: true });
    }
  }, [centro]);

  const parentOptions = useMemo(() => {
    return (centros ?? [])
      .filter((item) => !centro || item.id !== centro.id)
      .map((item) => ({ value: item.id, label: `${item.codigo} · ${item.nombre}` }));
  }, [centro, centros]);

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.codigo.trim() || !form.nombre.trim()) {
      toast.error('Completá código y nombre');
      return;
    }
    const payload = {
      codigo: form.codigo.trim(),
      nombre: form.nombre.trim(),
      padreId: form.padreId || null,
      activo: Boolean(form.activo),
    };
    try {
      const result = isEdit
        ? await actualizarCentro({ id: centro.id, input: payload })
        : await crearCentro({ input: { ...payload, empresaId: String(empresaId) } });
      if (result.error) {
        throw result.error;
      }
      toast.success(isEdit ? 'Centro actualizado' : 'Centro creado');
      onSuccess?.();
    } catch (error) {
      toast.error(error.message ?? 'No pudimos guardar el centro');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6">
      <section className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.88)] p-6 shadow-[0_20px_40px_-24px_rgba(var(--shadow),0.35)]">
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 md:col-span-6">
            <Field label="Código" hint="Único por empresa">
              <input
                value={form.codigo}
                onChange={(event) => handleChange('codigo', event.target.value)}
                className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                maxLength={20}
              />
            </Field>
          </div>
          <div className="col-span-12 md:col-span-6">
            <Field label="Nombre">
              <input
                value={form.nombre}
                onChange={(event) => handleChange('nombre', event.target.value)}
                className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                maxLength={120}
              />
            </Field>
          </div>
          <div className="col-span-12 md:col-span-6">
            <Field label="Centro padre">
              <select
                value={form.padreId}
                onChange={(event) => handleChange('padreId', event.target.value)}
                className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              >
                <option value="">Sin asignar</option>
                {parentOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <div className="col-span-12 md:col-span-6">
            <Field label="Estado">
              <label className="flex items-center gap-2 text-sm text-[rgb(var(--fg))]">
                <input
                  type="checkbox"
                  checked={form.activo}
                  onChange={(event) => handleChange('activo', event.target.checked)}
                  className="h-4 w-4 rounded border-[rgb(var(--border))] text-[rgb(var(--brand))] focus:ring-[rgb(var(--ring))]"
                />
                Activo
              </label>
            </Field>
          </div>
        </div>
      </section>

      <StickyActions
        left={
          <button
            type="button"
            onClick={onCancel}
            className="rounded-xl border border-[rgb(var(--border))] px-4 py-2 text-sm text-[rgb(var(--fg))] transition-colors hover:bg-[rgba(var(--muted),0.12)] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
          >
            Cancelar
          </button>
        }
        right={
          <button
            type="submit"
            className="rounded-xl bg-[rgb(var(--brand))] px-5 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[rgb(var(--brand-700))] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[rgb(var(--ring))]"
          >
            {isEdit ? 'Actualizar centro' : 'Crear centro'}
          </button>
        }
      />
    </form>
  );
}
